import { useEffect } from 'react';

/**
 * Le avisa al sitio que nos incrusta cuánto medimos, para que agrande el iframe.
 *
 * EL PROBLEMA QUE RESUELVE: el código de incrustación que el productor pega en
 * su sitio YA trae el listener que ajusta la altura del iframe...
 *
 *     if (e.data && e.data.height) { iframe.style.height = e.data.height + 'px'; }
 *
 * ...pero el micrositio nunca posteaba esa altura. El listener existía y jamás
 * se disparaba, así que el iframe quedaba clavado en el `height="800"` del
 * snippet: un catálogo de 217 animales dentro de una ventana de 800px, con
 * scroll anidado, que en mobile es directamente una trampa. El deep-link del
 * mismo script sí andaba, y por eso nadie lo notó — llegabas al animal
 * correcto, pero adentro de una caja.
 *
 * LO BUENO: el arreglo es sólo de este lado. Los sitios que ya tienen el
 * snippet pegado empiezan a ajustar solos en cuanto esto sale a producción, sin
 * que el productor vuelva a copiar nada.
 *
 * Se postea en cada cambio de alto (`ResizeObserver`), no una sola vez al
 * montar: las fotos entran después del primer render, los filtros achican la
 * grilla y el acordeón de condiciones cambia el alto sin recargar.
 */
export function useAvisarAltoAlPadre() {
  useEffect(() => {
    // Sin padre no hay a quién avisarle. Se chequea acá y no en el caller
    // para que las páginas del micrositio no repitan la condición.
    if (typeof window === 'undefined' || window.parent === window) return undefined;

    // Marca que estamos incrustados. Lo lee `index.css` para apagar el
    // `min-height:100vh` de las páginas del micrositio — ver el comentario
    // largo de arriba: sin eso el alto no baja nunca.
    document.documentElement.setAttribute('data-embed', '');

    // Se mide el BODY y no `documentElement`: el alto del `<html>` incluye el
    // viewport del iframe, así que después de que el padre lo agranda ya no
    // puede reportar menos. El body mide lo que realmente ocupa el contenido.
    const body = document.body;
    let ultimo = 0;

    const avisar = () => {
      const alto = Math.ceil(body.getBoundingClientRect().height);
      // Sólo si cambió de verdad: sin este corte, un ResizeObserver que se
      // dispara con el mismo valor genera un postMessage por frame.
      if (alto && alto !== ultimo) {
        ultimo = alto;
        window.parent.postMessage({ height: alto }, '*');
      }
    };

    avisar();
    const observer = new ResizeObserver(avisar);
    observer.observe(body);

    // `load` además del observer: las imágenes del catálogo cambian el alto
    // cuando terminan de bajar, y no siempre disparan un resize del root.
    window.addEventListener('load', avisar);

    return () => {
      observer.disconnect();
      window.removeEventListener('load', avisar);
      document.documentElement.removeAttribute('data-embed');
    };
  }, []);
}
